// Money helpers. Amounts are plain numbers in euros, always rounded to cents when stored.

// Rounds to cents like a calculator does (half up). Plain Math.round(x * 100) gets cases like 66.725 wrong, because
// the computer stores that number as 66.72499…; going through its 15-digit decimal form fixes that.
export const r2 = (x) => {
  if (typeof x !== 'number' || !Number.isFinite(x)) return 0;
  const a = Math.abs(x);
  if (a >= 1e12) return x;
  const cents = a < 1e-3 ? 0 : Math.round(Number(`${a.toPrecision(15)}e2`));
  const v = cents / 100;
  return x < 0 && v !== 0 ? -v : v; // never -0
};

export const sum = (xs) => r2(xs.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0));

const FORMATS = {
  'pt-PT': new Intl.NumberFormat('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }),
  'en-GB': new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true }),
};
let numberLocale = 'pt-PT';
export const setNumberLocale = (loc) => { if (FORMATS[loc]) numberLocale = loc; };
export const getNumberLocale = () => numberLocale;

// 1234.5 -> "1 234,50 €" (pt-PT) or "€1,234.50" (en-GB). Groups thousands always, which pt-PT skips for 4 digits.
export function fmt(x, { sign = false, blankZero = false, currency = true } = {}) {
  if (x === null || x === undefined || Number.isNaN(x)) return '—';
  const v = r2(x);
  if (blankZero && v === 0) return '';
  const abs = Math.abs(v);
  let body;
  if (numberLocale === 'pt-PT') {
    const [int, dec] = abs.toFixed(2).split('.');
    body = `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`;
    body = currency ? `${body} €` : body;
  } else {
    body = FORMATS['en-GB'].format(abs);
    body = currency ? `€${body}` : body;
  }
  const prefix = v < 0 ? '−' : sign && v > 0 ? '+' : '';
  return prefix + body;
}

// Plain number for inputs: "1234,50" (pt-PT) / "1234.50"
export function fmtPlain(x) {
  if (x === null || x === undefined || x === '' || Number.isNaN(x)) return '';
  const s = r2(x).toFixed(2).replace(/\.00$/, '');
  return numberLocale === 'pt-PT' ? s.replace('.', ',') : s;
}

export const pct = (x, digits = 1) => `${(Math.round(x * 10 ** digits) / 10 ** digits).toFixed(digits).replace(/\.0+$/, '')}%`;

/* ── Parsing what people type ─────────────────────────────
   Accepts "1513", "1513,5", "1.513,50", "1,513.50", "1 513,50 €", "€ 1513", "-20", "(20)", and simple sums like
   "1500 - 23,50 + 12" or "=200*3". Returns { ok: true, value } or { ok: false, error }. */

// One number, written with either decimal separator
export function parseNumberToken(raw) {
  let s = String(raw).replace(/[\s  ]/g, '').replace(/€|eur(os?)?/gi, '');
  if (!s) return null;
  if (!/^\d[\d.,]*$|^[.,]\d+$/.test(s)) return null;
  const commas = (s.match(/,/g) || []).length;
  const dots = (s.match(/\./g) || []).length;
  if (commas && dots) {
    const dec = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.';
    const thou = dec === ',' ? '.' : ',';
    const [int, frac = ''] = s.split(dec);
    if (s.split(dec).length > 2) return null;
    if (!/^\d{1,3}([.,]\d{3})*$|^\d+$/.test(int)) return null;
    s = `${int.split(thou).join('')}.${frac}`;
  } else if (commas) {
    // A single comma is a decimal comma (Portuguese style); several are thousands separators
    if (commas === 1) s = s.replace(',', '.');
    else if (/^\d{1,3}(,\d{3})+$/.test(s)) s = s.replace(/,/g, '');
    else return null;
  } else if (dots) {
    // "1.234" or "12.345.678" are thousands; "1.5", "1.50" and "0.123" are decimals
    if (dots > 1) {
      if (!/^\d{1,3}(\.\d{3})+$/.test(s)) return null;
      s = s.replace(/\./g, '');
    } else if (/^[1-9]\d{0,2}\.\d{3}$/.test(s)) s = s.replace('.', '');
  }
  if (s.startsWith('.')) s = `0${s}`;
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
}

function tokenize(input) {
  const tokens = [];
  const s = input;
  let i = 0;
  while (i < s.length) {
    const c = s[i];
    if (/[\s  ]/.test(c)) { i++; continue; }
    if ('+-*/()'.includes(c) || c === '×' || c === '÷' || c === '−' || c === 'x' || c === 'X') {
      const op = { '×': '*', x: '*', X: '*', '÷': '/', '−': '-' }[c] || c;
      tokens.push({ op });
      i++;
      continue;
    }
    if (/[\d.,]/.test(c)) {
      let j = i;
      // numbers may contain spaces as thousands separators only when followed by exactly 3 digits
      while (j < s.length && (/[\d.,]/.test(s[j]) || (/[\s  ]/.test(s[j]) && /^\d{3}(?!\d)/.test(s.slice(j + 1))))) j++;
      const v = parseNumberToken(s.slice(i, j));
      if (v === null) throw new Error(`"${s.slice(i, j).trim()}" isn't a number`);
      tokens.push({ num: v });
      i = j;
      continue;
    }
    throw new Error(`Unexpected "${c}"`);
  }
  return tokens;
}

function evaluate(tokens) {
  let pos = 0;
  const peek = () => tokens[pos];
  const take = () => tokens[pos++];
  function primary() {
    const t = take();
    if (!t) throw new Error('Incomplete');
    if (t.num !== undefined) return t.num;
    if (t.op === '-') return -primary();
    if (t.op === '+') return primary();
    if (t.op === '(') {
      const v = expr();
      if (!peek() || peek().op !== ')') throw new Error('Missing ")"');
      take();
      return v;
    }
    throw new Error('Incomplete');
  }
  function term() {
    let v = primary();
    while (peek() && (peek().op === '*' || peek().op === '/')) {
      const op = take().op;
      const rhs = primary();
      if (op === '/' && rhs === 0) throw new Error("Can't divide by zero");
      v = op === '*' ? v * rhs : v / rhs;
    }
    return v;
  }
  function expr() {
    let v = term();
    while (peek() && (peek().op === '+' || peek().op === '-')) {
      const op = take().op;
      const rhs = term();
      v = op === '+' ? v + rhs : v - rhs;
    }
    return v;
  }
  const v = expr();
  if (pos < tokens.length) throw new Error('Unexpected input');
  return v;
}

export function parseMoney(input, { allowNegative = true, allowEmpty = false, max = 10_000_000 } = {}) {
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return { ok: false, error: 'Not a number' };
    input = String(input);
  }
  let s = String(input ?? '').trim();
  if (!s) return allowEmpty ? { ok: true, value: null } : { ok: false, error: 'Enter an amount' };
  if (s.startsWith('=')) s = s.slice(1);
  s = s.replace(/€|eur(os?)?\b/gi, ' ');
  // (20) means -20, as in accounting; a trailing minus too
  let negate = false;
  if (/^\(.*\)$/.test(s.trim()) && !/[+*/×÷x]/i.test(s)) { s = s.trim().slice(1, -1); negate = true; }
  if (/^[^-]*\d\s*-$/.test(s.trim())) { s = s.trim().slice(0, -1); negate = true; }
  let v;
  try {
    v = evaluate(tokenize(s));
  } catch (e) {
    return { ok: false, error: e.message || 'Not a number' };
  }
  if (negate) v = -v;
  if (!Number.isFinite(v)) return { ok: false, error: 'Not a number' };
  v = r2(v);
  if (!allowNegative && v < 0) return { ok: false, error: "Can't be negative" };
  if (Math.abs(v) > max) return { ok: false, error: 'That amount is too large' };
  return { ok: true, value: v };
}

// Whole numbers of days etc.
export function parseCount(input, { min = 0, max = 31 } = {}) {
  const s = String(input ?? '').trim();
  if (!s) return { ok: true, value: 0 };
  const r = parseMoney(s);
  if (!r.ok) return r;
  const v = r.value;
  if (Math.abs(v - Math.round(v * 2) / 2) > 1e-9) return { ok: false, error: 'Use whole or half days' };
  if (v < min) return { ok: false, error: `At least ${min}` };
  if (v > max) return { ok: false, error: `At most ${max}` };
  return { ok: true, value: v };
}

export function parsePercent(input, { min = 0, max = 1000 } = {}) {
  const s = String(input ?? '').trim().replace(/%$/, '');
  const r = parseMoney(s, { allowNegative: min < 0 });
  if (!r.ok) return r;
  if (r.value < min) return { ok: false, error: `At least ${min}%` };
  if (r.value > max) return { ok: false, error: `At most ${max}%` };
  return r;
}
